import React from "react";
import { Heart, FileSignature, MessageCircle, Check } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import ShareMenu from "./ShareMenu";

const CLINIC_WA_E164 = "+447445372190";

/** Two-card section for Islamic Marriage services (Counselling + Nikah).
 *  Every card has both: (1) WhatsApp booking CTA, (2) Share menu (WhatsApp,
 *  Facebook, Copy Link, native share). Fully localised in EN/AR/UR/DE/FR. */
const MarriageServicesSection = () => {
  const { t } = useLanguage();

  const buildWa = (msg) =>
    `https://wa.me/${CLINIC_WA_E164.replace("+", "")}?text=${encodeURIComponent(msg)}`;

  const cards = [
    {
      id: "marriage-counselling",
      icon: Heart,
      accent: "#8C3A3A",
      title: t("counselling_title"),
      desc: t("counselling_desc"),
      wa: buildWa(t("counselling_book_wa_msg")),
      includes: [
        t("counselling_includes_1"),
        t("counselling_includes_2"),
        t("counselling_includes_3"),
        t("counselling_includes_4"),
      ],
    },
    {
      id: "islamic-nikah",
      icon: FileSignature,
      accent: "#2C5545",
      title: t("nikah_title"),
      desc: t("nikah_desc"),
      wa: buildWa(t("nikah_book_wa_msg")),
      includes: [
        t("nikah_includes_1"),
        t("nikah_includes_2"),
        t("nikah_includes_3"),
        t("nikah_includes_4"),
      ],
    },
  ];

  return (
    <section data-testid="marriage-services-section" className="mb-16">
      <div className="text-center mb-8">
        <p className="text-[11px] uppercase tracking-[0.3em] text-gold mb-2 font-semibold">
          {t("marriage_services_pill")}
        </p>
        <h2 className="font-heading text-3xl sm:text-4xl text-forest font-semibold">
          {t("marriage_services_title")}
        </h2>
        <p className="text-sm text-mute mt-3 max-w-2xl mx-auto">{t("marriage_services_subtitle")}</p>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {cards.map((c) => {
          const Icon = c.icon;
          const shareUrl = typeof window !== "undefined"
            ? `${window.location.origin}/services#${c.id}`
            : "";
          return (
            <article
              key={c.id}
              id={c.id}
              data-testid={`marriage-card-${c.id}`}
              className="bg-white rounded-3xl border border-gold/25 p-6 sm:p-7 shadow-sm flex flex-col"
            >
              <div className="flex items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-4 min-w-0">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: `${c.accent}18`, color: c.accent }}
                  >
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-heading text-lg sm:text-xl text-forest font-semibold leading-snug min-w-0">
                    {c.title}
                  </h3>
                </div>
                <ShareMenu title={c.title} url={shareUrl} compact />
              </div>

              <p className="text-sm text-mute leading-relaxed mb-4">{c.desc}</p>

              {/* Clear service conditions — makes it obvious what visitors get */}
              <div className="bg-cream/60 rounded-2xl p-4 mb-5">
                <p className="text-[10px] uppercase tracking-widest text-gold font-semibold mb-2">
                  {t("whats_included")}
                </p>
                <ul className="space-y-1.5">
                  {c.includes.map((line, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-ink">
                      <Check className="w-3.5 h-3.5 text-forest mt-0.5 flex-shrink-0" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <a
                data-testid={`marriage-book-${c.id}`}
                href={c.wa}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold text-sm px-6 py-3 rounded-full transition shadow-md self-start mt-auto"
              >
                <MessageCircle className="w-4 h-4" /> {t("book_on_whatsapp")}
              </a>
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default MarriageServicesSection;
